import { DragOverlay } from "@dnd-kit/core";
import { useRef, useMemo, useCallback } from "react";
import { FiMenu, FiX, FiEdit } from "react-icons/fi";
import { CSS } from "@dnd-kit/utilities";
import {
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { SortableContext } from "@dnd-kit/sortable";
import { IFormField } from "@/api/businessLinesApi";
import { renderField } from "./renderfield";

export const Dnd = ({
  fields,
  setFields,
  activeId,
  setActiveId,
  isReadOnly = false,
}: {
  fields: IFormField[];
  setFields: (fields: IFormField[]) => void;
  activeId: string | null;
  setActiveId: (activeId: string | null) => void;
  isReadOnly?: boolean;
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Increased distance to prevent accidental drags
        delay: 100, // Add delay to allow text selection
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const handleFieldUpdate = useCallback(
    (updatedField: IFormField) => {
      if (isReadOnly) return;

      console.log("Dnd: handleFieldUpdate called", {
        updatedFieldId: updatedField.id,
        updatedFieldTitle: updatedField.title,
        currentFieldsLength: fieldsRef.current.length,
      });

      const newFields = fieldsRef.current.map((field) =>
        field.id === updatedField.id ? updatedField : field
      );
      console.log("Dnd: Setting new fields", {
        newFieldsLength: newFields.length,
        newFields: newFields.map((f) => ({ id: f.id, title: f.title })),
      });
      setFields(newFields);
    },
    [setFields, isReadOnly]
  );

  // Memoize the fields mapping to prevent unnecessary re-renders
  const sortableItems = useMemo(() => {
    return fields.map((field) => (
      <SortableItem
        key={field.id}
        id={field.id!}
        field={field}
        onFieldUpdate={handleFieldUpdate}
        fields={fields}
        setFields={setFields}
        isReadOnly={isReadOnly}
      />
    ));
  }, [fields, setFields, handleFieldUpdate, isReadOnly]);

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnly) return;
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnly) return;

    const { active, over } = event;
    console.log("Dnd: handleDragEnd called", {
      activeId: active.id,
      overId: over?.id,
      fieldsLength: fields.length,
    });

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((item) => item.id === active.id);
      const newIndex = fields.findIndex((item) => item.id === over.id);
      console.log("Dnd: Moving field", { oldIndex, newIndex });
      const newFields = arrayMove(fields, oldIndex, newIndex);
      console.log("Dnd: New fields after move", {
        newFieldsLength: newFields.length,
        newFields: newFields.map((f) => ({ id: f.id, title: f.title })),
      });
      setFields(newFields);
    }

    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={fields.map((field) => {
          return field.id!;
        })}
        strategy={verticalListSortingStrategy}
      >
        {sortableItems}
      </SortableContext>
      <DragOverlay>
        {activeId ? (
          <div className="bg-[#F8F9FC] rounded-lg overflow-hidden mb-4">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="text-gray-400">
                  <FiMenu size={20} />
                </div>
                <h3 className="text-gray-700 font-medium">
                  {fields.find((field) => field.id === activeId)?.title}
                </h3>
              </div>
              <button
                className="text-gray-400"
                title="Close"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

const SortableItem = ({
  id,
  field,
  onFieldUpdate,
  fields,
  setFields,
  isReadOnly = false,
}: {
  id: string;
  field: IFormField;
  onFieldUpdate: (updatedField: IFormField) => void;
  fields: IFormField[];
  setFields: (fields: IFormField[]) => void;
  isReadOnly?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Use field directly as the source of truth, no local state to avoid loops
  const currentField = field;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleFieldTypeChange = (newType: IFormField["field_type"]) => {
    const updatedField = {
      ...currentField,
      field_type: newType,
      choices:
        newType === "radio" || newType === "checkbox" || newType === "select"
          ? [""]
          : null,
    };
    onFieldUpdate(updatedField);
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="mb-4">
      <div className="bg-[#F8F9FC] rounded-lg overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className="text-gray-400 cursor-grab hover:text-gray-600 transition-colors"
              {...(isReadOnly ? {} : listeners)}
              title="Drag to reorder"
            >
              <FiMenu size={20} />
            </div>
            <h3 className="text-gray-700 font-medium">
              {field.id?.startsWith("my-") ? currentField.title : field.title}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {field.id?.startsWith("my-") && !isReadOnly && (
              <button
                className="text-gray-400 hover:text-gray-600"
                title="Edit field"
                aria-label="Edit field"
              >
                <FiEdit size={20} />
              </button>
            )}
            {!isReadOnly && (
              <button
                onClick={() => {
                  setFields(fields.filter((f) => f.id !== id));
                }}
                className="text-gray-400 hover:text-gray-600"
                title="Delete field"
                aria-label="Delete field"
              >
                <FiX size={20} />
              </button>
            )}
          </div>
        </div>
        <div className="p-4">
          {renderField({
            field,
            editedField: currentField,
            setEditedField: (updatedField: IFormField) =>
              onFieldUpdate(updatedField),
            handleFieldTypeChange,
            onFieldUpdate,
            isReadOnly,
          })}
        </div>
      </div>
    </div>
  );
};
